import type { Instruction } from '@/features/instructions/types'
import { useMe } from '@/features/auth/useMe'
import { WebInstructionCard } from '@/features/instructions/components/editor/preview/web/WebInstructionCard'
import { CoverShapes } from '@/components/catalog/CoverPlaceholder'
import { useElementWidth } from '@/lib/useElementWidth'
import { AccessBadge } from './list/AccessBadge'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Ширина карточки в разделе инструкций — под неё нарисован чип доступа. */
const LIST_CARD_W = 240
/** Обложка в единицах карточки: одна 337-я ширины (`webCover.module.css`). */
const COVER_UNITS = 337
const SELECT_GAP = 4

interface Props {
  article: Instruction
  /** Рамка выбора — того же скругления, что обложка. */
  selected?: boolean
}

/**
 * Обложка статьи — та же карточка, что в разделе инструкций (значок доступа,
 * обложка, название, автор, просмотры). Родитель задаёт размер (`aspect-[4/5]`).
 * Обложка масштабируется от своей ширины, поэтому чип доступа и рамка выбора
 * считаются от неё же — иначе на широкой карточке чип мелкий, а рамка кривая.
 */
export const ArticleCover = ({ article, selected = false }: Props) => {
  const { data: me } = useMe()
  const m = useMessages(messages)
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const scale = width > 0 ? width / LIST_CARD_W : 1
  const radius = ((article.coverType === 'type2' ? 12 : 16) * width) / COVER_UNITS
  return (
    <div ref={ref} className="relative size-full">
      {selected ? (
        <div
          aria-hidden
          className="pointer-events-none absolute border-2 border-brand"
          style={{ inset: -SELECT_GAP - 2, borderRadius: radius + SELECT_GAP + 2 }}
        />
      ) : null}
      <div
        className="absolute left-0 top-0 z-20 origin-top-left"
        style={{ width: LIST_CARD_W, transform: `scale(${scale})` }}
      >
        <AccessBadge accessType={article.accessType} />
      </div>
      <WebInstructionCard
        coverType={article.coverType === 'type2' ? 'type2' : 'type1'}
        showHeart={false}
        placeholder={<CoverShapes />}
        title={article.title || m.newInstruction}
        gradient={article.gradient}
        cardImage={article.cardImage}
        titleColor={article.titleColor}
        authorName={article.author?.name ?? me?.username ?? m.author}
        authorAvatar={article.author?.avatar ?? me?.avatar ?? null}
        viewsCount={article.viewsCount ?? 0}
      />
    </div>
  )
}
